/**
 * Settings Types for SalonHub
 * Issue #238: 包括的設定管理システム
 * 
 * HITOYASUMIでの実用的な店舗管理に必要な全設定項目を定義
 */

// =============================================================================
// 🏪 店舗基本設定
// =============================================================================

export interface BusinessHours {
  [key: string]: {
    isOpen: boolean;
    openTime: string;  // "10:00" format
    closeTime: string; // "20:00" format
    breakStart?: string;
    breakEnd?: string;
  };
}

export interface Address {
  postalCode: string;
  prefecture: string;
  city: string;
  address1: string;
  address2?: string;
  building?: string;
}

export interface ContactInfo {
  phone: string;
  email: string;
  website?: string;
  instagram?: string;
  twitter?: string;
  line?: string;
}

export interface StoreSettings {
  // 基本情報
  storeName: string;
  storeType: string;
  description?: string;
  
  // 営業情報
  businessHours: BusinessHours;
  holidays: string[];
  timezone: string;
  
  // 連絡先・住所
  address: Address;
  contactInfo: ContactInfo;
  
  // 財務設定
  taxRate: number;
  currency: string;
  roundingRule: 'round' | 'floor' | 'ceil';
  
  // 予約設定
  advanceBookingDays: number;
  cancellationPolicy: {
    hours: number;
    penaltyRate: number;
  };
  
  // 表示設定
  language: 'ja' | 'en';
  dateFormat: string;
  timeFormat: '12h' | '24h';
}

// =============================================================================
// 👥 ユーザー・権限管理
// =============================================================================

export type UserRole = 'owner' | 'manager' | 'staff' | 'receptionist';

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: 'customer' | 'reservation' | 'financial' | 'settings' | 'reports';
}

export interface RolePermissions {
  role: UserRole;
  permissions: string[]; // Permission IDs
  canViewReports: boolean;
  canManageStaff: boolean;
  canAccessFinancials: boolean;
  canModifySettings: boolean;
}

export interface StaffAccount {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: UserRole;
  isActive: boolean;
  lastLogin?: Date;
  createdAt: Date;
  permissions: string[];
  
  // スタッフ特有設定
  workingHours?: BusinessHours;
  specialties: string[];
  commissionRate?: number;
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  expirationDays: number;
  preventReuse: number;
}

export interface SessionPolicy {
  timeoutMinutes: number;
  maxConcurrentSessions: number;
  requireReauthForSensitive: boolean;
}

export interface UserManagementSettings {
  staffAccounts: StaffAccount[];
  rolePermissions: RolePermissions[];
  passwordPolicy: PasswordPolicy;
  sessionPolicy: SessionPolicy;
  twoFactorEnabled: boolean;
  auditLogging: boolean;
}

// =============================================================================
// 💾 データ管理設定
// =============================================================================

export interface BackupConfig {
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string; // "03:00" format
  retentionDays: number;
  includeFiles: boolean;
  encryptBackups: boolean;
  cloudStorage?: 'aws' | 'gcp' | 'azure';
}

export interface RetentionPolicy {
  customerData: number; // days
  reservationHistory: number;
  paymentRecords: number;
  medicalRecords: number;
  treatmentNotes: number;
  auditLogs: number;
  autoDeleteEnabled: boolean;
}

export interface EncryptionSettings {
  algorithm: string;
  keyRotationDays: number;
  encryptSensitiveFields: boolean;
  encryptBackups: boolean;
  encryptExports: boolean;
}

export interface ExportConfig {
  format: 'csv' | 'json' | 'xml' | 'pdf';
  includePersonalData: boolean;
  includeFinancialData: boolean;
  includeMedicalData: boolean;
  passwordProtect: boolean;
  scheduledExports: {
    enabled: boolean;
    frequency: 'daily' | 'weekly' | 'monthly';
    recipients: string[];
  };
}

export interface DataManagementSettings {
  backup: BackupConfig;
  retention: RetentionPolicy;
  encryption: EncryptionSettings;
  export: ExportConfig[];
  gdprCompliance: {
    enabled: boolean;
    dataProcessingBasis: string;
    retentionJustification: string;
    rightToErasure: boolean;
    dataPortability: boolean;
  };
}

// =============================================================================
// 🔔 通知・連携設定
// =============================================================================

export interface EmailConfig {
  enabled: boolean;
  fromAddress: string;
  fromName: string;
  templates: {
    reservationConfirmation: boolean;
    reservationReminder: boolean;
    cancellationNotice: boolean;
    treatmentCompletion: boolean;
    marketingEmails: boolean;
  };
  // Note: SMTP configuration (host, port, username, password) is managed on the backend only
  // See server-config.ts for backend email configuration
}

export interface SMSConfig {
  enabled: boolean;
  provider: 'twilio' | 'sendgrid' | 'aws-sns';
  fromNumber?: string;
  templates: {
    reservationReminder: boolean;
    cancellationAlert: boolean;
    emergencyNotice: boolean;
  };
  // Note: API keys and authentication are managed on the backend only
  // See server-config.ts for backend SMS configuration
}

export interface ReminderConfig {
  email: {
    enabled: boolean;
    timing: number[]; // hours before appointment
  };
  sms: {
    enabled: boolean;
    timing: number[];
  };
  push: {
    enabled: boolean;
    timing: number[];
  };
}

export interface IntegrationConfig {
  hotpepper: {
    enabled: boolean;
    syncFrequency: 'realtime' | 'hourly' | 'daily';
    // Note: API keys are managed on the backend only
  };
  rakuten: {
    enabled: boolean;
    syncFrequency: 'realtime' | 'hourly' | 'daily';
    // Note: API keys are managed on the backend only
  };
  google: {
    enabled: boolean;
    businessProfile: boolean;
    reservations: boolean;
    // Note: Service account keys are managed on the backend only
  };
  stripe: {
    enabled: boolean;
    publicKey?: string;  // Public key is safe for frontend
    // Note: Secret key and webhook secret are managed on the backend only
    // See server-config.ts for complete Stripe configuration
  };
}

export interface NotificationSettings {
  email: EmailConfig;
  sms: SMSConfig;
  push: {
    enabled: boolean;
    // Note: VAPID keys are loaded from environment variables
    // Public key is accessible via env.get('VAPID_PUBLIC_KEY')
  };
  reminders: ReminderConfig;
  integrations: IntegrationConfig;
  
  // 内部通知設定
  staffNotifications: {
    newReservation: boolean;
    cancellation: boolean;
    noShow: boolean;
    paymentFailed: boolean;
  };
}

// =============================================================================
// 🤖 AI・分析設定
// =============================================================================

export interface ConsentConfig {
  dataCollection: boolean;
  analyticsSharing: boolean;
  marketingUse: boolean;
  thirdPartySharing: boolean;
  customerConsentRequired: boolean;
}

export interface AIAnalysisConfig {
  customerInsights: 'disabled' | 'basic' | 'advanced';
  salesForecasting: boolean;
  inventoryOptimization: boolean;
  staffScheduleOptimization: boolean;
  marketingRecommendations: boolean;
}

export interface AISettings {
  enabled: boolean;
  analysisLevel: AIAnalysisConfig;
  dataConsent: ConsentConfig;
  
  // AI履歴・キャッシュ管理
  cacheRetentionDays: number;
  analysisFrequency: 'realtime' | 'hourly' | 'daily' | 'weekly';
  
  // AI機能制限
  monthlyQueryLimit?: number;
  advancedFeaturesEnabled: boolean;
  
  // プライバシー設定
  anonymizeData: boolean;
  localProcessingOnly: boolean;
}

// =============================================================================
// 📱 UI/表示設定
// =============================================================================

export interface DisplaySettings {
  theme: 'light' | 'dark' | 'auto';
  primaryColor: string;
  accentColor: string;
  fontSize: 'small' | 'medium' | 'large';
  
  // ダッシュボード設定
  dashboardLayout: 'standard' | 'compact' | 'detailed';
  defaultView: 'dashboard' | 'calendar' | 'customers';
  
  // モバイル設定
  mobileOptimized: boolean;
  pwaEnabled: boolean;
  offlineMode: boolean;
}

// =============================================================================
// 🔧 システム設定
// =============================================================================

export interface SystemSettings {
  // パフォーマンス
  cacheEnabled: boolean;
  cacheTTL: number;
  maxConcurrentUsers: number;
  
  // セキュリティ
  auditLogEnabled: boolean;
  bruteForceProtection: boolean;
  ipWhitelist?: string[];
  
  // メンテナンス
  maintenanceMode: boolean;
  maintenanceMessage?: string;
  healthCheckEnabled: boolean;
  
  // 開発者設定
  debugMode: boolean;
  apiLogging: boolean;
  errorReporting: boolean;
}

// =============================================================================
// 📋 統合Settings型
// =============================================================================

export interface ComprehensiveSettings {
  // 基本設定
  store: StoreSettings;
  users: UserManagementSettings;
  data: DataManagementSettings;
  notifications: NotificationSettings;
  ai: AISettings;
  display: DisplaySettings;
  system: SystemSettings;
  
  // メタデータ
  version: string;
  lastUpdated: Date;
  updatedBy: string;
}

// =============================================================================
// 🛡️ 権限チェック用ヘルパー型
// =============================================================================

export interface SettingsAccess {
  canViewStore: boolean;
  canEditStore: boolean;
  canManageUsers: boolean;
  canConfigureIntegrations: boolean;
  canAccessDataSettings: boolean;
  canModifyAISettings: boolean;
  canViewSystemSettings: boolean;
  canEditSystemSettings: boolean;
}

// =============================================================================
// 📝 設定変更ログ
// =============================================================================

export interface SettingsChangeLog {
  id: string;
  timestamp: Date;
  userId: string;
  userName: string;
  section: keyof ComprehensiveSettings;
  field: string;
  oldValue?: unknown;
  newValue: unknown;
  ipAddress?: string;
  userAgent?: string;
}

// デフォルト設定値
export const DEFAULT_SETTINGS: ComprehensiveSettings = {
  store: {
    storeName: 'ドライヘッドスパHITOYASUMI 渋谷店',
    storeType: 'ヘッドスパ専門店',
    businessHours: {
      monday: { isOpen: false, openTime: '10:00', closeTime: '20:00' },
      tuesday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
      wednesday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
      thursday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
      friday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
      saturday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
      sunday: { isOpen: true, openTime: '10:00', closeTime: '20:00' },
    },
    holidays: ['monday'],
    timezone: 'Asia/Tokyo',
    address: {
      postalCode: '150-0043',
      prefecture: '東京都',
      city: '渋谷区',
      address1: '道玄坂1-2-3',
      building: '渋谷ビル4F',
    },
    contactInfo: {
      phone: '03-1234-5678',
      email: 'contact@hitoyasumi.jp',
      website: 'https://hitoyasumi.jp',
    },
    taxRate: 0.10,
    currency: 'JPY',
    roundingRule: 'round',
    advanceBookingDays: 30,
    cancellationPolicy: {
      hours: 24,
      penaltyRate: 0.5,
    },
    language: 'ja',
    dateFormat: 'YYYY/MM/DD',
    timeFormat: '24h',
  },
  users: {
    staffAccounts: [],
    rolePermissions: [],
    passwordPolicy: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: false,
      expirationDays: 90,
      preventReuse: 3,
    },
    sessionPolicy: {
      timeoutMinutes: 60,
      maxConcurrentSessions: 3,
      requireReauthForSensitive: true,
    },
    twoFactorEnabled: false,
    auditLogging: true,
  },
  data: {
    backup: {
      enabled: true,
      frequency: 'daily',
      time: '03:00',
      retentionDays: 30,
      includeFiles: false,
      encryptBackups: true,
    },
    retention: {
      customerData: 2555, // 7 years
      reservationHistory: 1095, // 3 years
      paymentRecords: 2555, // 7 years
      medicalRecords: 1825, // 5 years
      treatmentNotes: 365, // 1 year
      auditLogs: 90,
      autoDeleteEnabled: false,
    },
    encryption: {
      algorithm: 'AES-256',
      keyRotationDays: 90,
      encryptSensitiveFields: true,
      encryptBackups: true,
      encryptExports: true,
    },
    export: [],
    gdprCompliance: {
      enabled: true,
      dataProcessingBasis: 'legitimate_interest',
      retentionJustification: 'business_operation',
      rightToErasure: true,
      dataPortability: true,
    },
  },
  notifications: {
    email: {
      enabled: false,
      fromAddress: 'noreply@hitoyasumi.jp',
      fromName: 'HITOYASUMI',
      templates: {
        reservationConfirmation: true,
        reservationReminder: true,
        cancellationNotice: true,
        treatmentCompletion: false,
        marketingEmails: false,
      },
    },
    sms: {
      enabled: false,
      provider: 'twilio',
      templates: {
        reservationReminder: false,
        cancellationAlert: false,
        emergencyNotice: false,
      },
    },
    push: {
      enabled: false,
    },
    reminders: {
      email: {
        enabled: false,
        timing: [24, 2],
      },
      sms: {
        enabled: false,
        timing: [24],
      },
      push: {
        enabled: false,
        timing: [24, 2],
      },
    },
    integrations: {
      hotpepper: {
        enabled: false,
        syncFrequency: 'daily',
      },
      rakuten: {
        enabled: false,
        syncFrequency: 'daily',
      },
      google: {
        enabled: false,
        businessProfile: false,
        reservations: false,
      },
      stripe: {
        enabled: false,
      },
    },
    staffNotifications: {
      newReservation: true,
      cancellation: true,
      noShow: true,
      paymentFailed: true,
    },
  },
  ai: {
    enabled: true,
    analysisLevel: {
      customerInsights: 'basic',
      salesForecasting: false,
      inventoryOptimization: false,
      staffScheduleOptimization: false,
      marketingRecommendations: false,
    },
    dataConsent: {
      dataCollection: true,
      analyticsSharing: false,
      marketingUse: false,
      thirdPartySharing: false,
      customerConsentRequired: true,
    },
    cacheRetentionDays: 30,
    analysisFrequency: 'daily',
    advancedFeaturesEnabled: false,
    anonymizeData: true,
    localProcessingOnly: false,
  },
  display: {
    theme: 'light',
    primaryColor: '#2c5530',
    accentColor: '#8bc34a',
    fontSize: 'medium',
    dashboardLayout: 'standard',
    defaultView: 'dashboard',
    mobileOptimized: true,
    pwaEnabled: true,
    offlineMode: false,
  },
  system: {
    cacheEnabled: true,
    cacheTTL: 3600,
    maxConcurrentUsers: 50,
    auditLogEnabled: true,
    bruteForceProtection: true,
    maintenanceMode: false,
    healthCheckEnabled: true,
    debugMode: false,
    apiLogging: false,
    errorReporting: true,
  },
  version: '1.0.0',
  lastUpdated: new Date(),
  updatedBy: 'system',
};